//
/* GS://PROTOCOLO
 * Arquivo: src/pages/lots/LotDetailPage.jsx
 * Dossiê de Projeto e Manual de Colaboração: Ecossistema GoldenSisters
 * Versão: 1.2.0 – UI: Botão de Integração de Estoque
 * Impacto da Versão: [src/pages/lots/LotDetailPage.jsx]
 * Data: 26/12/2025
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - ADICIONADO: Botão "Integrar ao Estoque" no cabeçalho.
 * - LÓGICA: O botão só aparece se houver itens "PRONTA" e o status for "ABERTO".
 * - UX: Feedback de carregamento e sucesso ao processar o lote.
 */

import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, PackageCheck } from 'lucide-react';
import { getLotById, getLotItems, integrateReadyItemsToStock } from '../../services/lotsService';

const formatCurrency = (value) => {
  const num = Number(value || 0);
  return `R$ ${num.toFixed(2).replace('.', ',')}`;
};

const formatWeight = (value) => {
  const num = Number(value || 0);
  return num.toFixed(2).replace('.', ',');
};

const formatLotDateDisplay = (lot) => {
  if (!lot) return 'N/A';
  if (lot.nfDateDisplay) return lot.nfDateDisplay;
  if (lot.nfDateISO) {
    const d = new Date(lot.nfDateISO);
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString('pt-BR');
  }
  if (lot.nfDate && typeof lot.nfDate.toDate === 'function') {
    try {
      const d = lot.nfDate.toDate();
      if (!Number.isNaN(d.getTime())) return d.toLocaleDateString('pt-BR');
    } catch { }
  }
  return 'N/A';
};

const LotDetailPage = () => {
  const { lotId } = useParams();

  const [lot, setLot] = useState(null);
  const [items, setItems] = useState([]);
  const [loadingLot, setLoadingLot] = useState(true);
  const [loadingItems, setLoadingItems] = useState(true);
  
  // [GS-NOVO]: Estado para controle de integração
  const [isIntegrating, setIsIntegrating] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lotId]);

  const loadData = () => {
      if (lotId) {
        loadLot();
        loadItems();
      }
  };

  const loadLot = async () => {
    setLoadingLot(true);
    try {
      const data = await getLotById(lotId);
      setLot(data);
    } catch (error) {
      console.error('[LotDetailPage] Erro ao carregar lote:', error);
    } finally {
      setLoadingLot(false);
    }
  };

  const loadItems = async () => {
    setLoadingItems(true);
    try {
      const data = await getLotItems(lotId);
      setItems(data);
    } catch (error) {
      console.error('[LotDetailPage] Erro ao carregar itens do lote:', error);
    } finally {
      setLoadingItems(false);
    }
  };

  // [GS-NOVO]: Ação de Integrar
  const handleIntegrateStock = async () => {
      if (!window.confirm("Deseja criar produtos no estoque para os itens PRONTA ENTREGA deste lote?")) {
          return;
      }

      setIsIntegrating(true);
      try {
          const result = await integrateReadyItemsToStock(lotId);
          alert(`Sucesso! ${result.count} produtos foram integrados ao estoque.`);
          loadData(); // Recarrega para atualizar status
      } catch (error) {
          alert(`Erro ao integrar: ${error.message}`);
      } finally {
          setIsIntegrating(false);
      }
  };

  const counts = lot?.counts || {
    totalItensNF: 0,
    qtdBrutas: 0,
    qtdProntas: 0,
    qtdServicos: 0,
  };

  const totals = lot?.totals || {
    subtotalItens: 0,
    totalRateio: 0,
    totalGeral: 0,
  };

  const statusBadgeColor = (() => {
    switch (lot?.statusLot) {
      case 'EM_BANHO': return 'bg-yellow-500 text-brand-black';
      case 'MONTAGEM': return 'bg-blue-500 text-white';
      case 'FINALIZADO': return 'bg-green-500 text-white';
      default: return 'bg-gray-700 text-gray-100';
    }
  })();

  // [GS-NOVO]: Verifica se pode mostrar botão de integração
  const canIntegrate = lot?.statusLot === 'ABERTO' && counts.qtdProntas > 0;

  return (
    <div className="p-6 text-white bg-brand-black min-h-screen">
      {/* Topo: voltar */}
      <div className="mb-4">
        <Link
          to="/lotes"
          className="inline-flex items-center text-sm text-gray-300 hover:text-brand-gold"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar para lista de lotes
        </Link>
      </div>

      {/* Cabeçalho principal */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">
            {lot ? `Lote ${lot.lotCode}` : 'Carregando lote...'}
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            NF {lot?.nfNumber || '—'} — Data: {formatLotDateDisplay(lot)}
          </p>
          <p className="text-sm text-gray-400">
            Fornecedor: {lot?.supplierName || 'N/A'}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {loadingLot ? (
            <div className="flex items-center space-x-2">
              <Loader2 className="animate-spin text-brand-gold" size={20} />
              <span className="text-sm text-gray-300">Carregando...</span>
            </div>
          ) : (
            <div className="flex items-center gap-3">
                {/* [GS-NOVO]: Botão de Ação */}
                {canIntegrate && (
                    <button 
                        onClick={handleIntegrateStock}
                        disabled={isIntegrating}
                        className="flex items-center gap-2 px-4 py-2 bg-brand-gold text-brand-black font-bold rounded hover:bg-yellow-500 transition disabled:opacity-50"
                    >
                        {isIntegrating ? <Loader2 className="animate-spin" size={18} /> : <PackageCheck size={18} />}
                        {isIntegrating ? 'Processando...' : 'Integrar ao Estoque'}
                    </button>
                )}

                <span className={`px-4 py-2 rounded-full text-xs font-semibold uppercase ${statusBadgeColor}`}>
                  Status {lot?.statusLot || 'DESCONHECIDO'}
                </span>
            </div>
          )}
        </div>
      </div>

      {/* Cards de totais e contagens */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Card Itens NF */}
        <div className="bg-brand-dark border border-gray-700 rounded-lg p-4">
          <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">ITENS NF</p>
          {loadingLot ? (
            <Loader2 className="animate-spin text-brand-gold" size={24} />
          ) : (
            <>
              <p className="text-3xl font-bold mb-2">{counts.totalItensNF}</p>
              <p className="text-xs text-gray-500">
                BRUTA: {counts.qtdBrutas} · PRONTA: {counts.qtdProntas} · SERVIÇO: {counts.qtdServicos}
              </p>
            </>
          )}
        </div>

        {/* Card Subtotal itens */}
        <div className="bg-brand-dark border border-gray-700 rounded-lg p-4">
          <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">SUBTOTAL ITENS NF</p>
          {loadingLot ? (
            <Loader2 className="animate-spin text-brand-gold" size={24} />
          ) : (
            <>
              <p className="text-2xl font-bold mb-1">{formatCurrency(totals.subtotalItens)}</p>
              <p className="text-xs text-gray-500">Valor bruto dos itens, antes do rateio.</p>
            </>
          )}
        </div>

        {/* Card Custo total do lote */}
        <div className="bg-brand-dark border border-gray-700 rounded-lg p-4">
          <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">CUSTO TOTAL DO LOTE</p>
          {loadingLot ? (
            <Loader2 className="animate-spin text-brand-gold" size={24} />
          ) : (
            <>
              <p className="text-2xl font-bold mb-1">{formatCurrency(totals.totalGeral)}</p>
              <p className="text-xs text-gray-500">Rateio total: {formatCurrency(totals.totalRateio)}.</p>
            </>
          )}
        </div>
      </div>

      {/* Itens do Lote */}
      <div className="bg-brand-dark border border-gray-700 rounded-lg p-4">
        <h2 className="text-xl font-semibold mb-2">Itens do Lote</h2>
        <p className="text-xs text-gray-500 mb-4">
          Origem direta da NF. Somente leitura. Base para BANHO / MONTAGEM / ESTOQUE.
        </p>

        {loadingItems ? (
          <div className="flex justify-center py-10">
            <Loader2 className="animate-spin text-brand-gold" size={32} />
          </div>
        ) : items.length === 0 ? (
          <p className="text-gray-400 text-center py-6">Nenhum item encontrado para este lote.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-700 text-gray-400 text-xs uppercase">
                  <th className="py-2 pr-2">#</th>
                  <th className="py-2 pr-2">Tipo</th>
                  <th className="py-2 pr-2">Descrição NF</th>
                  <th className="py-2 pr-2">Cód. Fornecedor</th>
                  <th className="py-2 pr-2">Categoria</th>
                  <th className="py-2 pr-2 text-right">Qtde</th>
                  <th className="py-2 pr-2 text-right">Peso (g)</th>
                  <th className="py-2 pr-2 text-right">Valor Unit. NF</th>
                  <th className="py-2 pr-2 text-right">Total NF</th>
                  <th className="py-2 pr-2 text-right">Rateio</th>
                  <th className="py-2 pr-2 text-right">Custo Total Inicial</th>
                  <th className="py-2 pr-2">Status Inicial</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => (
                  <tr key={item.id} className="border-b border-gray-800 hover:bg-gray-900/60">
                    <td className="py-2 pr-2 text-xs text-gray-500">{item.nfItemIndex || index + 1}</td>
                    <td className="py-2 pr-2 text-xs">
                        <span className={`px-2 py-0.5 rounded ${item.itemType === 'PRONTA' ? 'bg-green-900 text-green-200' : 'bg-gray-700'}`}>
                            {item.itemType || '—'}
                        </span>
                    </td>
                    <td className="py-2 pr-2">{item.descriptionNF || '—'}</td>
                    <td className="py-2 pr-2 text-xs">{item.supplierProductCode || '—'}</td>
                    <td className="py-2 pr-2 text-xs">{item.categoryId || '—'}</td>
                    <td className="py-2 pr-2 text-right">{item.quantity}</td>
                    <td className="py-2 pr-2 text-right">{formatWeight(item.weightGrams)}</td>
                    <td className="py-2 pr-2 text-right">{formatCurrency(item.unitPriceNF)}</td>
                    <td className="py-2 pr-2 text-right">{formatCurrency(item.totalItemNF)}</td>
                    <td className="py-2 pr-2 text-right">{formatCurrency(item.rateioPerItem)}</td>
                    <td className="py-2 pr-2 text-right">{formatCurrency(item.costTotalInicial)}</td>
                    <td className="py-2 pr-2 text-xs">{item.statusInicial || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LotDetailPage;